import * as React from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useLocation } from 'js/lib/useRouter';

import { IconButton } from '@coursera/cds-core';
import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import useCoachTriggerMessage from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useCoachTriggerMessage';
import StoreProviderWrapper from 'bundles/ai-coach-platform/components/patterns/chat/store/StoreProviderWrapper';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import SparkleOpenIcon from 'bundles/unified-common/components/coach-on-discovery/SparkleOpenIcon';
import useNavAiSparkleSurfaceInfo from 'bundles/unified-common/hooks/useNavAiSparkleSurfaceInfo';
import { useGlobalCoachChatState } from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import { useNavAiSparkle } from 'bundles/unified-common/providers/NavAiSparkleExperimentProvider';

import _t from 'i18n!nls/composable-page-header';

const COACH_CONFIG = {
  coachModeId: 'CONVERSATIONAL_DISCOVERY',
  coachActionId: 'TYPED_MESSAGE',
};

const ROUTER_COACH_CONFIG = {
  coachModeId: 'AI_SEARCH_V2',
  coachActionId: 'ROUTER',
};

const useSparkleClick = () => {
  const location = useLocation();
  const { coachOpen, setCoachOpen, contextConfig, setTriggerElementRef, pageChatType, setContextConfig } =
    useGlobalCoachChatState();
  const track = useTracker();
  const surfaceInfoContext = useNavAiSparkleSurfaceInfo();

  const handleOpen = useCallback<() => void>(() => {
    track('interact_coach', {
      coachInteractionType: 'click_action',
      coach: {
        mode: contextConfig.config.coachModeId,
        location: 'toolbar',
      },
      coachAction: contextConfig.config.coachActionId,
    });
    setContextConfig({ config: ROUTER_COACH_CONFIG });
    setTriggerElementRef(document.activeElement instanceof HTMLElement ? document.activeElement : null);
    setCoachOpen((prev) => !prev);
  }, [
    track,
    setTriggerElementRef,
    setCoachOpen,
    setContextConfig,
    contextConfig.config.coachModeId,
    contextConfig.config.coachActionId,
  ]);

  const onTriggerMessage = useCoachTriggerMessage({
    config: COACH_CONFIG,
    additionalContext: [...(contextConfig.additionalContext ?? []), ...(surfaceInfoContext ?? [])],
    courseraContext: contextConfig.courseraContext,
    onClick: handleOpen,
    enableTextStreaming: true,
    enableLoggedOutMode: true,
    location,
  });

  return useCallback<() => void>(() => {
    if (pageChatType === 'side-by-side' || coachOpen) {
      handleOpen();
    } else {
      // pageChatType is 'fullscreen' or null (global fallback)
      onTriggerMessage({
        message: _t('Help me explore.'),
        label: _t('Ask Coursera'),
      });
    }
  }, [pageChatType, coachOpen, handleOpen, onTriggerMessage]);
};

const NavAiSparkleGridItemContent = () => {
  const onSparkleClick = useSparkleClick();
  const tooltip = _t('Ask Coursera');
  const { coachOpen } = useGlobalCoachChatState();
  const { isMessageLoading, isMessageStreaming } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
    isMessageStreaming: state.behavior.isMessageStreaming,
  }));

  const disableButton = !coachOpen && (isMessageLoading || isMessageStreaming);

  return (
    <IconButton
      aria-label={tooltip}
      tooltip={tooltip}
      onClick={onSparkleClick}
      data-testid="coursera-ai-sparkle"
      disabled={disableButton}
      variant="ghost"
      icon={coachOpen ? <SparkleOpenIcon size="large" /> : <AIGenerateBrandedNewIcon size="large" />}
    />
  );
};

const NavAiSparkleGridItem = () => {
  const { showNavAiSparkle } = useNavAiSparkle();
  if (!showNavAiSparkle) return null;

  return (
    <StoreProviderWrapper>
      <NavAiSparkleGridItemContent />
    </StoreProviderWrapper>
  );
};

export default NavAiSparkleGridItem;
